import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError, api } from './api'
import type { MeResponse } from '../contracts/api'
import { TabBar } from './components/TabBar'
import type { TabId } from './components/TabBar'
import { NavContext, useBackButton } from './nav'
import type { Nav, NavTarget, Screen } from './nav'
import { Access } from './screens/Access'
import { Admin } from './screens/Admin'
import { Chapter } from './screens/Chapter'
import { Crossings } from './screens/Crossings'
import { CyclePeriod } from './screens/CyclePeriod'
import { Cycles } from './screens/Cycles'
import { Journal } from './screens/Journal'
import { LifeScale } from './screens/LifeScale'
import { FirstLook } from './screens/FirstLook'
import { Me } from './screens/Me'
import { Onboarding } from './screens/Onboarding'
import { Oracle } from './screens/Oracle'
import { Room } from './screens/Room'
import { Rooms } from './screens/Rooms'
import { SymbolScreen } from './screens/Symbol'
import { Symbols } from './screens/Symbols'
import { Today } from './screens/Today'
import { We } from './screens/We'

type Status = 'loading' | 'ready' | 'error'

export function App() {
  const [status, setStatus] = useState<Status>('loading')
  const [error, setError] = useState('')
  const [me, setMe] = useState<MeResponse | null>(null)
  const [justBuilt, setJustBuilt] = useState(false)

  // Навигация: вкладка плюс стопка вложенных экранов поверх неё. Стопка, а не
  // одно поле, потому что «Расчёты → комната» — два шага, и системная стрелка
  // Telegram обязана вернуть на список, а не сразу во вкладку.
  const [tab, setTab] = useState<TabId>('today')
  const [stack, setStack] = useState<Screen[]>([])
  const screen = stack.at(-1) ?? null

  const navigate = useCallback((target: NavTarget) => {
    if (target.tab) setTab(target.tab)
    setStack((current) => {
      // Экран не назван: смена вкладки сбрасывает вложенное, иначе всё как было.
      if (target.screen === undefined) return target.tab ? [] : current
      if (target.screen === null) return []
      return [...current, target.screen]
    })
  }, [])

  const back = useCallback(() => {
    setStack((current) => current.slice(0, -1))
  }, [])

  const nav = useMemo<Nav>(() => ({ tab, screen, navigate, back }), [tab, screen, navigate, back])

  useBackButton(screen ? back : null)

  // Админка открывается в обычном браузере, где никакого Telegram нет:
  // её нельзя пускать через проверку initData.
  const isAdminPath = window.location.pathname.startsWith('/admin')

  useEffect(() => {
    if (isAdminPath) return undefined
    let cancelled = false
    api
      .get<MeResponse>('/api/me')
      .then((data) => {
        if (cancelled) return
        setMe(data)
        setStatus('ready')
      })
      .catch((cause: unknown) => {
        if (cancelled) return
        setError(cause instanceof ApiError ? cause.message : 'Не получилось загрузить Атлас')
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [isAdminPath])

  if (isAdminPath) return <Admin />
  if (status === 'loading') return <div className="state">Собираем твой Атлас…</div>
  if (status === 'error' || !me) return <div className="state">{error}</div>

  // Пока данных рождения нет — весь экран занимает знакомство. Вкладок в этот
  // момент не существует: человеку нечего в них смотреть.
  if (!me.profile) {
    return (
      <Onboarding
        onDone={(profile) => {
          setMe({ ...me, profile })
          setJustBuilt(true)
        }}
      />
    )
  }

  // Печать показывается сразу после анкеты — это первый настоящий результат.
  if (justBuilt) return <FirstLook onOpen={() => setJustBuilt(false)} />

  return (
    <NavContext.Provider value={nav}>
      {screen ? renderScreen(screen) : renderTab(tab, me)}
      <TabBar active={tab} onChange={(next) => navigate({ tab: next })} />
    </NavContext.Provider>
  )
}

function renderTab(tab: TabId, me: MeResponse): ReactNode {
  switch (tab) {
    case 'today':
      return <Today me={me} />
    case 'cycles':
      return <Cycles />
    case 'oracle':
      return <Oracle me={me} />
    case 'we':
      return <We me={me} />
    case 'me':
      return <Me me={me} />
    default: {
      const unhandled: never = tab
      void unhandled
      return null
    }
  }
}

/**
 * Вложенный экран поверх вкладки. Разбираются все варианты Screen: проверка
 * never внизу не даёт завести новый экран и забыть его нарисовать.
 */
function renderScreen(screen: Screen): ReactNode {
  switch (screen.kind) {
    case 'chapter':
      return <Chapter id={screen.id} title={screen.title} />
    case 'rooms':
      return <Rooms />
    case 'room':
      return <Room system={screen.system} />
    case 'cycle':
      return <CyclePeriod period={screen.period} />
    case 'lifescale':
      return <LifeScale />
    case 'symbols':
      return <Symbols />
    case 'symbol':
      return <SymbolScreen id={screen.id} />
    case 'journal':
      return <Journal />
    case 'crossings':
      return <Crossings />
    case 'access':
      return <Access />
    default: {
      const unhandled: never = screen
      void unhandled
      return null
    }
  }
}
